import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Alert,
  AlertDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';

// ─── Types ─────────────────────────────────────────────────────────────────────

type StepStatus = 'completed' | 'pending' | 'not_started';

interface QualificationStep {
  id: string;
  title: string;
  description: string;
  status: StepStatus;
  completedDate?: string;
  iconName: string;
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const INITIAL_STEPS: QualificationStep[] = [
  {
    id: 'kyc',
    title: 'KYC Complete',
    description: 'Identity verification through our KYC provider has been completed and approved.',
    status: 'completed',
    completedDate: '2026-02-15',
    iconName: 'streamline:interface-user-check-actions-close-checkmark-check-geometric-human-person-single-success-up-user',
  },
  {
    id: 'jurisdiction',
    title: 'Jurisdiction Approved',
    description: 'Your jurisdiction has been verified and approved for participation in credit facilities.',
    status: 'completed',
    completedDate: '2026-02-18',
    iconName: 'streamline:travel-map-earth-2-planet-earth-globe-world',
  },
  {
    id: 'investor-type',
    title: 'Investor Type Verified',
    description: 'Verification of your investor classification (accredited, qualified purchaser, etc.) is in progress.',
    status: 'pending',
    iconName: 'streamline:shield-check',
  },
  {
    id: 'claims',
    title: 'Claims Valid',
    description: 'On-chain claims must be issued and validated to complete your qualification.',
    status: 'not_started',
    iconName: 'streamline:interface-file-text-text-common-file',
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<StepStatus, { label: string; className: string; iconName: string }> = {
  completed: {
    label: 'Completed',
    className: 'bg-green-100 text-green-700',
    iconName: 'streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form',
  },
  pending: {
    label: 'Pending',
    className: 'bg-yellow-100 text-yellow-700',
    iconName: 'streamline:interface-time-clock-circle-clock-loading-measure-time-circle',
  },
  not_started: {
    label: 'Not Started',
    className: 'bg-gray-100 text-gray-600',
    iconName: 'streamline:circle',
  },
};

function getOverallStatus(steps: QualificationStep[]): 'qualified' | 'pending' | 'incomplete' {
  if (steps.every((s) => s.status === 'completed')) return 'qualified';
  if (steps.some((s) => s.status === 'pending' || s.status === 'completed')) return 'pending';
  return 'incomplete';
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function QualificationPage() {
  const [steps, setSteps] = useState(INITIAL_STEPS);
  const [checking, setChecking] = useState(false);

  const overallStatus = getOverallStatus(steps);
  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);

  function handleCheckQualification() {
    setChecking(true);
    setTimeout(() => {
      setSteps((prev) =>
        prev.map((step) =>
          step.id === 'investor-type'
            ? { ...step, status: 'completed' as StepStatus, completedDate: '2026-03-23' }
            : step,
        ),
      );
      setChecking(false);
    }, 2000);
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:shield-check" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Qualification Status</h1>
            <p className="text-sm text-muted-foreground">Track your qualification progress for credit facility access.</p>
          </div>
        </div>
        <Button onClick={handleCheckQualification} disabled={checking}>
          {checking ? <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />Checking...</> : 'Check Qualification'}
        </Button>
      </div>

      {/* Overall status badge */}
      <div className="flex justify-center">
        <div className={`inline-flex items-center gap-3 rounded-2xl px-8 py-4 ${
          overallStatus === 'qualified'
            ? 'bg-ascend-success/20 text-ascend-success'
            : overallStatus === 'pending'
              ? 'bg-ascend-warning/20 text-ascend-warning'
              : 'bg-gray-100 text-gray-600'
        }`}>
          <Icon name="streamline:shield-check" className="h-8 w-8" />
          <div>
            <div className="text-lg font-bold capitalize">
              {overallStatus === 'qualified' ? 'Qualified' : overallStatus === 'pending' ? 'Pending Qualification' : 'Incomplete'}
            </div>
            <div className="text-sm opacity-80">{completedCount} of {steps.length} steps completed</div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div className="mb-2 flex justify-between text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium">{progressPercent}%</span>
        </div>
        <div className="h-3 w-full rounded-full bg-muted">
          <div
            className="h-3 rounded-full bg-green-500 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Qualification steps */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle>Qualification Checklist</CardTitle>
          <CardDescription>Complete all steps to qualify for full facility access.</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-1">
            {steps.map((step, index) => {
              const config = STATUS_CONFIG[step.status];
              const isLast = index === steps.length - 1;

              return (
                <div key={step.id} className="relative flex gap-4">
                  {/* Vertical connector */}
                  <div className="flex flex-col items-center">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                      step.status === 'completed'
                        ? 'border-green-500 bg-green-50'
                        : step.status === 'pending'
                          ? 'border-yellow-500 bg-yellow-50'
                          : 'border-gray-300 bg-gray-50'
                    }`}>
                      <Icon name={step.iconName} className={`h-5 w-5 ${
                        step.status === 'completed' ? 'text-green-600' :
                        step.status === 'pending' ? 'text-yellow-600' : 'text-gray-400'
                      }`} />
                    </div>
                    {!isLast && (
                      <div className={`w-0.5 flex-1 min-h-[24px] ${
                        step.status === 'completed' ? 'bg-green-300' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>

                  {/* Content */}
                  <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-6'}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold">{step.title}</h3>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>
                        <Icon name={config.iconName} className="h-3 w-3" />
                        {config.label}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{step.description}</p>
                    {step.completedDate && (
                      <p className="mt-1 text-xs text-muted-foreground">Completed on {step.completedDate}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Next steps guidance */}
      {overallStatus !== 'qualified' && (
        <Alert>
          <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-4 w-4" />
          <AlertDescription>
            <strong>Next Steps:</strong>{' '}
            {steps.find((s) => s.status === 'pending')
              ? `Your "${steps.find((s) => s.status === 'pending')!.title}" step is currently under review. This typically takes 1-3 business days. You'll be notified once it's complete.`
              : 'Complete the remaining qualification steps to gain access to credit facilities. Start with your KYC verification if you haven\'t already.'
            }
          </AlertDescription>
        </Alert>
      )}

      {overallStatus === 'qualified' && (
        <Alert>
          <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-4 w-4" />
          <AlertDescription>
            <strong>Congratulations!</strong> You are fully qualified. You can now access all available credit facilities and create margin accounts.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
